/**
 * computePDOFitDelta — derive the strategy-vs-execution comparison.
 *
 * Walks the brief angle's intended PDO factors (driving_pdo_factors) against
 * the creative review's detected concepts (linked_concepts). Each factor is
 * classified as match | gap | surprise:
 *   - match    — intended AND detected
 *   - gap      — intended but not detected (execution shortfall)
 *   - surprise — detected but not intended (unexpected signal)
 *
 * Sort order: gaps first (most actionable), then matches, then surprises.
 *
 * Backwards-compat: the BriefAngle Pydantic model in the backend currently
 * exposes `concept_ids: string[]`, not `driving_pdo_factors`. When only
 * concept_ids are present we synthesize minimal PDOFactor rows so the UI
 * can still classify match/gap.
 */

import type {
  BriefPDOFactor,
  LinkedConcept,
  PDOFitConcept,
  WorkspaceBriefAngle,
} from '../types';

function intendedFactors(angle: WorkspaceBriefAngle | null): BriefPDOFactor[] {
  if (!angle) return [];
  if (angle.driving_pdo_factors && angle.driving_pdo_factors.length > 0) {
    return angle.driving_pdo_factors;
  }
  if (angle.concept_ids && angle.concept_ids.length > 0) {
    return angle.concept_ids.map((cid) => ({
      concept_id: cid,
      concept_name: cid.replace(/_/g, ' '),
      category: 'ATTRIBUTE',
      edge_type: 'DRIVES',
    }));
  }
  return [];
}

function conceptKey(c: LinkedConcept): string {
  return c.concept_id ?? c.id;
}

export function computePDOFitDelta(
  briefAngle: WorkspaceBriefAngle | null,
  linkedConcepts: LinkedConcept[],
): PDOFitConcept[] {
  const intended = intendedFactors(briefAngle);
  const detected = new Map<string, LinkedConcept>();
  for (const c of linkedConcepts) {
    const key = conceptKey(c);
    if (key) detected.set(key, c);
  }

  const intendedIds = new Set(intended.map((f) => f.concept_id));
  const results: PDOFitConcept[] = [];

  // Matches and gaps (walk intended factors).
  for (const factor of intended) {
    const detectedConcept = detected.get(factor.concept_id);
    results.push({
      concept_id: factor.concept_id,
      concept_name: factor.concept_name ?? factor.concept_id,
      category: (factor.category ?? 'ATTRIBUTE') as PDOFitConcept['category'],
      edge_type: (factor.edge_type ?? 'DRIVES') as PDOFitConcept['edge_type'],
      status: detectedConcept ? 'match' : 'gap',
      brief_confidence: factor.activation_strength,
      detected_confidence: detectedConcept?.binding_confidence ?? undefined,
      detected_evidence:
        detectedConcept?.evidence ?? detectedConcept?.evidence_text ?? undefined,
    });
  }

  // Surprises — detected concepts that aren't in the brief intent.
  for (const c of linkedConcepts) {
    const cid = conceptKey(c);
    if (!cid || intendedIds.has(cid)) continue;
    results.push({
      concept_id: cid,
      concept_name: c.name,
      category: ((c.entity_type ?? 'ATTRIBUTE') as string).toUpperCase() as PDOFitConcept['category'],
      edge_type: (c.edge_type ?? 'DRIVES') as PDOFitConcept['edge_type'],
      status: 'surprise',
      detected_confidence: c.binding_confidence ?? undefined,
      detected_evidence: c.evidence ?? c.evidence_text ?? undefined,
    });
  }

  const sortPriority: Record<PDOFitConcept['status'], number> = {
    gap: 0,
    match: 1,
    surprise: 2,
  };
  return results.sort((a, b) => sortPriority[a.status] - sortPriority[b.status]);
}
